import * as THREE from 'three';

type SpiralParams = {
  count: number; // total points in this geometry
  arms: number; // spiral arms
  turns: number; // revolutions center -> rim
  innerRadius: number; // r at center
  outerRadius: number; // r at rim
  armSpread: number; // angular std-dev grows slightly with radius
  radialJitter: number; // fraction of r
  zSpreadMin: number; // vertical thickness near center (std-dev)
  zSpreadMax: number; // vertical thickness at rim (std-dev)
  biasToCenterExp: number; // >1 packs density toward center
  colorInner?: number; // hex color near center
  colorOuter?: number; // hex color at rim
};

function randn() {
  let u = 0,
    v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

function createSpiralGeometry(p: SpiralParams) {
  const {
    count,
    arms,
    turns,
    innerRadius,
    outerRadius,
    armSpread,
    radialJitter,
    zSpreadMin,
    zSpreadMax,
    biasToCenterExp,
    colorInner = 0xffeecc,
    colorOuter = 0xaaccff,
  } = p;

  const totalTheta = turns * Math.PI * 2;
  const r0 = Math.max(0.0001, innerRadius);
  const k = Math.log(outerRadius / r0) / totalTheta; // r = r0 * exp(k*theta)

  const pos = new Float32Array(count * 3);
  const col = new Float32Array(count * 3);
  const cInner = new THREE.Color(colorInner);
  const cOuter = new THREE.Color(colorOuter);

  let ptr = 0;
  for (let i = 0; i < count; i++) {
    // choose an arm
    const arm = Math.floor(Math.random() * arms);
    const armPhase = (arm / arms) * Math.PI * 2;

    // angle with center-biased density
    const u = Math.random();
    const thetaBase = Math.pow(u, biasToCenterExp) * totalTheta;

    // base radius on log spiral + jitter
    const r = r0 * Math.exp(k * thetaBase);
    const spread = armSpread * (0.6 + 0.4 * (r / outerRadius));
    const theta = thetaBase + armPhase + randn() * spread;
    const rf = Math.max(0, r + r * (randn() * radialJitter));

    // z thickness grows outward
    const zSigma = zSpreadMin + (zSpreadMax - zSpreadMin) * (rf / outerRadius);
    const x = rf * Math.cos(theta);
    const y = rf * Math.sin(theta);
    // make sure z is less than 0
    const z = randn() * zSigma;

    pos[ptr] = x;
    pos[ptr + 1] = y;
    pos[ptr + 2] = z;

    // color blend by radius
    const t = Math.min(1, rf / outerRadius);
    const c = cInner.clone().lerp(cOuter, t);
    col[ptr] = c.r;
    col[ptr + 1] = c.g;
    col[ptr + 2] = c.b;

    ptr += 3;
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geom.setAttribute('color', new THREE.BufferAttribute(col, 3));

  // big bounding sphere so the LOD object isn't culled early
  geom.computeBoundingSphere();
  if (geom.boundingSphere) geom.boundingSphere.radius = outerRadius * 1.2;

  return geom;
}

type StarfieldLODOpts = {
  // base spiral look
  arms?: number;
  turns?: number;
  innerRadius?: number;
  outerRadius?: number;
  armSpread?: number;
  radialJitter?: number;
  zSpreadMin?: number;
  zSpreadMax?: number;
  biasToCenterExp?: number;

  // LOD & material
  totalCount?: number; // max stars at closest zoom
  sizes?: [number, number, number]; // star size per LOD level
  distances?: [number, number, number]; // LOD switch distances (far->near)
  transparent?: boolean;
  additive?: boolean;
};

export function addSpiralStarfieldLOD(scene: THREE.Scene, opts: StarfieldLODOpts = {}) {
  const {
    arms = 3,
    turns = 4,
    innerRadius = 5,
    outerRadius = 1500,
    armSpread = 0.12,
    radialJitter = 0.06,
    zSpreadMin = 2.0,
    zSpreadMax = 25.0,
    biasToCenterExp = 2.2,

    totalCount = 48000,
    // LOD order: Level 0 (far), Level 1 (mid), Level 2 (near)
    sizes = [1.6, 1.3, 1.1],
    distances = [8000, 4000, 0], // in world units from camera to LOD.position
    transparent = true,
    additive = false,
  } = opts;

  // densities per level (sparse -> dense)
  const counts = [
    Math.floor(totalCount * 0.1), // far
    Math.floor(totalCount * 0.55), // mid
    Math.floor(totalCount * 1.0), // near
  ];

  const lod = new THREE.LOD();
  lod.name = 'SpiralStarfieldLOD';

  for (let level = 0; level < 3; level++) {
    const geom = createSpiralGeometry({
      count: counts[level],
      arms,
      turns,
      innerRadius,
      outerRadius,
      armSpread,
      radialJitter,
      zSpreadMin,
      zSpreadMax,
      biasToCenterExp,
    });

    const mat = new THREE.PointsMaterial({
      size: sizes[level],
      sizeAttenuation: true,
      vertexColors: true,
      depthWrite: false, // don't occlude scene
      depthTest: true,
      transparent,
      opacity: 0.95,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
    });

    const points = new THREE.Points(geom, mat);
    points.frustumCulled = false; // keep as “sky” even if mostly around you
    points.renderOrder = -9999; // draw behind other stuff

    // Add this level to the LOD with its switch distance
    lod.addLevel(points, distances[level]);
  }

  // Place the LOD at the center of your world/galaxy (usually 0,0,0).
  // As you dolly in with OrbitControls, the camera–>LOD distance shrinks,
  // and Three.js automatically swaps to denser levels.
  lod.position.set(0, 0, 0);
  scene.add(lod);

  return lod;
}

export function addStarfieldBackground(scene: THREE.Scene) {
  const starsGeometry = new THREE.BufferGeometry();
  const starCount = 25000;

  const positions = new Float32Array(starCount * 3);
  for (let i = 0; i < starCount * 3; i++) {
    positions[i] = (Math.random() - 0.5) * 5000;
  }

  starsGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const starsMaterial = new THREE.PointsMaterial({
    color: 0xffffff,
    size: 2,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0.7,
  });

  const stars = new THREE.Points(starsGeometry, starsMaterial);
  scene.add(stars);
}
